import React, { useEffect, useMemo, useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { Toast } from "primereact/toast";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import QuotationService from "../../services/QuotationService";
import SaleService from "../../services/SaleService";
import ProductService from "../../services/ProductService";
import QuotationReceipt from "../../components/Quotation/QuotationReceipt";

export default function QuotationAddEditPage() {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    const navigate = useNavigate();
    const { id: editId } = useParams();

    const toast = useRef(null);
    const receiptRef = useRef(null);

    const customerInputRef = useRef(null);
    const productInputRef = useRef(null);
    const quantityRef = useRef(null);
    const priceRef = useRef(null);

    const [isDirty, setIsDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [lastQuotationId, setLastQuotationId] = useState(null);

    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerQuery, setCustomerQuery] = useState("");
    const [customers, setCustomers] = useState([]);
    const [showCustomerList, setShowCustomerList] = useState(false);

    const [productQuery, setProductQuery] = useState("");
    const [products, setProducts] = useState([]);
    const [showList, setShowList] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(0);

    const [header, setHeader] = useState({
        quotationDate: new Date(),
        validUntil: null,
        note: "",
        terms: "",
        quotationNo: ""
    });

    const [lines, setLines] = useState([]);
    const [generalDiscount, setGeneralDiscount] = useState(0);

    const [newLine, setNewLine] = useState({
        product: null,
        quantity: 1,
        unitPrice: 0,
        vatRate: 0,
        note: "",
        priceAlternatives: [],
        priceTierId: null,
        originalResolvedPrice: 0
    });

    const overlayAppendTo = mounted ? document.body : "self";

    const calendarOverlayProps = {
        appendTo: overlayAppendTo,
        autoZIndex: false,
        baseZIndex: 12000,
        panelClassName: "quotation-calendar-panel"
    };

    const moneyFormat = (value) => {
        return Number(value || 0).toLocaleString("tr-TR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const focusAndSelect = (ref) => {
        if (!ref.current) return;

        const inputEl =
            ref.current.getInput?.() ||
            ref.current.input?.current ||
            ref.current.input;

        if (inputEl && inputEl.select) {
            inputEl.focus();
            inputEl.select();
        } else {
            ref.current.focus?.();
        }
    };

    const updateHeader = (field, value) => {
        setHeader((prev) => ({
            ...prev,
            [field]: value
        }));
        setIsDirty(true);
    };

    const updateNewLine = (field, value) => {
        setNewLine((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    useEffect(() => {
        if (editId) {
            QuotationService.get(editId).then((res) => {
                if (res.success && res.data) {
                    const d = res.data;

                    setSelectedCustomer({
                        id: d.customerId,
                        name: d.customerName
                    });

                    setCustomerQuery(d.customerName || "");

                    setHeader({
                        quotationDate: d.quotationDate ? new Date(d.quotationDate) : new Date(),
                        validUntil: d.validUntil ? new Date(d.validUntil) : null,
                        note: d.note || "",
                        terms: d.terms || "",
                        quotationNo: d.quotationNo || ""
                    });

                    setGeneralDiscount(d.discountTotal || 0);

                    const loadLinesAlternatives = async (items, customerId) => {
                        const mappedLines = await Promise.all((items || []).map(async (item, idx) => {
                            let priceAlternatives = [];
                            let priceTierId = item.priceTierId;
                            try {
                                const priceRes = await ProductService.getPrice(item.productId, customerId);
                                if (priceRes.data) {
                                    priceAlternatives = priceRes.data.priceAlternatives || [];
                                }
                            } catch (err) {
                                console.error("Fiyat detayı alınamadı", err);
                            }
                            return {
                                id: `line-${idx}-${Date.now()}`,
                                productId: item.productId,
                                productName: item.productName,
                                quantity: item.quantity,
                                unitPrice: item.unitPrice,
                                vatRate: item.vatRate || 0,
                                note: item.note || "",
                                supplierId: item.supplierId || 0,
                                priceAlternatives: priceAlternatives,
                                priceTierId: priceTierId,
                                originalPrice: item.unitPrice
                            };
                        }));
                        setLines(mappedLines);
                    };
                    loadLinesAlternatives(d.items, d.customerId);

                    setIsDirty(false);
                }
            });
        } else {
            setTimeout(() => customerInputRef.current?.focus(), 100);
        }
    }, [editId]);

    // Refs to avoid stale state in keyboard shortcuts
    const newLineRef = useRef(newLine);
    const selectedCustomerRef = useRef(selectedCustomer);

    useEffect(() => {
        newLineRef.current = newLine;
    }, [newLine]);

    useEffect(() => {
        selectedCustomerRef.current = selectedCustomer;
    }, [selectedCustomer]);

    // Keyboard shortcut Alt + [1-9] for price tiers
    useEffect(() => {
        const handlePriceShortcut = (e) => {
            const currentNewLine = newLineRef.current;
            if (!currentNewLine || !currentNewLine.product) return;

            if (e.altKey && e.key >= "1" && e.key <= "9") {
                const index = parseInt(e.key) - 1;

                if (index === 0) {
                    e.preventDefault();
                    const targetPrice = currentNewLine.originalResolvedPrice || 0;
                    const targetTierId = selectedCustomerRef.current?.defaultPriceTierId || null;

                    setNewLine((prev) => ({
                        ...prev,
                        unitPrice: targetPrice,
                        priceTierId: targetTierId
                    }));

                    toast.current?.show({
                        severity: "info",
                        summary: "Fiyat Değiştirildi",
                        detail: `Standart Fiyat: ${targetPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺`,
                        life: 1500
                    });
                } else {
                    const altIdx = index - 1;
                    if (currentNewLine.priceAlternatives && currentNewLine.priceAlternatives[altIdx]) {
                        e.preventDefault();
                        const altPriceObj = currentNewLine.priceAlternatives[altIdx];

                        setNewLine((prev) => ({
                            ...prev,
                            unitPrice: altPriceObj.priceInclVat,
                            priceTierId: altPriceObj.priceTierId
                        }));

                        toast.current?.show({
                            severity: "info",
                            summary: "Fiyat Değiştirildi",
                            detail: `${altPriceObj.priceTierName}: ${altPriceObj.priceInclVat.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺`,
                            life: 1500
                        });
                    }
                }
            }
        };

        window.addEventListener("keydown", handlePriceShortcut);
        return () => window.removeEventListener("keydown", handlePriceShortcut);
    }, []);


    const searchCustomers = async (query) => {
        try {
            if (!query || query.trim().length < 1) {
                setCustomers([]);
                setShowCustomerList(false);
                return;
            }

            const list = await SaleService.searchCustomer({
                params: {
                    query,
                    take: 20
                }
            });

            setCustomers(list || []);
            setShowCustomerList((list || []).length > 0);
        } catch (err) {
            setCustomers([]);
            setShowCustomerList(false);
        }
    };

    const selectCustomer = (customer) => {
        setSelectedCustomer(customer);
        setCustomerQuery(customer?.name || "");
        setCustomers([]);
        setShowCustomerList(false);
        setIsDirty(true);

        setTimeout(() => productInputRef.current?.focus(), 100);
    };

    const searchProducts = async (query) => {
        try {
            if (!query || query.trim().length < 2) {
                setProducts([]);
                setShowList(false);
                return;
            }

            const list = await SaleService.searchProduct({
                params: {
                    query,
                    take: 20,
                    customerId: selectedCustomer?.id || null
                }
            });

            setProducts(list || []);
            setShowList((list || []).length > 0);
            setHighlightIndex(0);
        } catch (err) {
            setProducts([]);
            setShowList(false);
        }
    };

    const selectProduct = async (product) => {
        if (!product) return;

        let resolvedPrice = product.salePrice || 0;
        let priceAlternatives = [];
        try {
            const priceRes = await ProductService.getPrice(product.id, selectedCustomer?.id || null);
            if (priceRes.data) {
                resolvedPrice = priceRes.data.salePrice;
                priceAlternatives = priceRes.data.priceAlternatives || [];
            }
        } catch (err) {
            console.error("Fiyat detayı alınamadı", err);
        }

        setNewLine((prev) => ({
            ...prev,
            product,
            unitPrice: resolvedPrice,
            vatRate: product.vatRate || 0,
            quantity: 1,
            priceAlternatives: priceAlternatives,
            priceTierId: selectedCustomer?.defaultPriceTierId || null,
            originalResolvedPrice: resolvedPrice
        }));

        setProductQuery(product.name || "");
        setProducts([]);
        setShowList(false);

        setTimeout(() => focusAndSelect(quantityRef), 100);
    };

    const handleAddLine = () => {
        if (!newLine.product) {
            toast.current?.show({
                severity: "warn",
                summary: "Uyarı",
                detail: "Lütfen ürün seçin.",
                life: 2500
            });
            return;
        }

        if (!newLine.quantity || newLine.quantity <= 0) {
            toast.current?.show({
                severity: "warn",
                summary: "Uyarı",
                detail: "Miktar 0'dan büyük olmalıdır.",
                life: 2500
            });
            return;
        }

        setLines((prev) => [
            {
                id: Date.now(),
                productId: newLine.product.id,
                productName: newLine.product.name,
                quantity: newLine.quantity,
                unitPrice: newLine.unitPrice || 0,
                vatRate: newLine.vatRate || 0,
                note: newLine.note || "",
                supplierId: newLine.product.supplierId || 0,
                priceAlternatives: newLine.priceAlternatives || [],
                priceTierId: newLine.priceTierId,
                originalPrice: newLine.unitPrice || 0
            },
            ...prev
        ]);

        setNewLine({
            product: null,
            quantity: 1,
            unitPrice: 0,
            vatRate: 0,
            note: "",
            priceAlternatives: [],
            priceTierId: null,
            originalResolvedPrice: 0
        });

        setProductQuery("");
        setIsDirty(true);

        setTimeout(() => productInputRef.current?.focus(), 0);
    };

    const totals = useMemo(() => {
        let sub = 0;
        let vat = 0;

        lines.forEach((l) => {
            const quantity = Number(l.quantity || 0);
            const unitPrice = Number(l.unitPrice || 0);
            const vatRate = Number(l.vatRate || 0);

            const lineSub = quantity * unitPrice;
            const lineVat = lineSub * (vatRate / 100);

            sub += lineSub;
            vat += lineVat;
        });

        const discount = Number(generalDiscount || 0);
        const grand = sub - discount + vat;

        return {
            sub,
            vat,
            discount,
            grand
        };
    }, [lines, generalDiscount]);

    const handleSave = async () => {
        if (!selectedCustomer?.id) {
            toast.current?.show({
                severity: "warn",
                summary: "Uyarı",
                detail: "Lütfen müşteri seçin.",
                life: 3000
            });
            return;
        }

        if (!lines.length) {
            toast.current?.show({
                severity: "warn",
                summary: "Uyarı",
                detail: "En az 1 ürün eklenmelidir.",
                life: 3000
            });
            return;
        }

        if (!header.validUntil) {
            toast.current?.show({
                severity: "warn",
                summary: "Uyarı",
                detail: "Geçerlilik tarihi zorunludur.",
                life: 3000
            });
            return;
        }

        const payload = {
            id: editId || 0,
            customerId: selectedCustomer?.id,
            quotationNo: header.quotationNo,
            quotationDate: header.quotationDate,
            validUntil: header.validUntil,
            note: header.note,
            terms: header.terms,
            discountTotal: generalDiscount,
            items: lines.map((l) => ({
                productId: l.productId,
                productName: l.productName,
                quantity: l.quantity,
                unitPrice: l.unitPrice,
                vatRate: l.vatRate,
                note: l.note,
                supplierId: l.supplierId,
                priceTierId: l.priceTierId
            }))
        };

        try {
            setSaving(true);

            const res = editId
                ? await QuotationService.update(payload)
                : await QuotationService.create(payload);

            if (res.success) {
                toast.current?.show({
                    severity: "success",
                    summary: "Başarılı",
                    detail: "Teklif kaydedildi.",
                    life: 3000
                });

                setIsDirty(false);

                const qid = res.data || editId;
                setLastQuotationId(qid);

                if (editId) {
                    setTimeout(() => navigate("/quotations"), 1200);
                }
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Hata",
                    detail: res.message || "Teklif kaydedilemedi.",
                    life: 3000
                });
            }
        } catch (err) {
            toast.current?.show({
                severity: "error",
                summary: "Hata",
                detail: "Kaydedilirken hata oluştu.",
                life: 3000
            });
        } finally {
            setSaving(false);
        }
    };

    const deleteLine = (event, id) => {
        confirmPopup({
            target: event.currentTarget,
            message: "Bu satırı silmek istediğinize emin misiniz?",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Sil",
            rejectLabel: "Vazgeç",
            acceptClassName: "p-button-danger",
            accept: () => {
                setLines((prev) => prev.filter((l) => l.id !== id));
                setIsDirty(true);
            }
        });
    };

    const productNameTemplate = (row) => {
        return (
            <div className="flex flex-col min-w-0">
                <span className="font-black text-slate-800 text-sm truncate">
                    {row.productName}
                </span>

                {row.note && (
                    <span className="text-[11px] text-slate-400 italic truncate mt-0.5">
                        {row.note}
                    </span>
                )}
            </div>
        );
    };

    const quantityTemplate = (row) => {
        return (
            <span className="font-black text-slate-700">
                {Number(row.quantity || 0).toLocaleString("tr-TR", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 3
                })}
            </span>
        );
    };

    const unitPriceTemplate = (row) => {
        return (
            <span className="font-bold text-slate-700 whitespace-nowrap">
                {moneyFormat(row.unitPrice)} ₺
            </span>
        );
    };

    const vatTemplate = (row) => {
        return (
            <Tag
                value={`%${Number(row.vatRate || 0)}`}
                severity="info"
                className="text-[10px] font-black"
            />
        );
    };

    const totalTemplate = (row) => {
        const total = Number(row.quantity || 0) * Number(row.unitPrice || 0);

        return (
            <span className="font-black text-slate-900 whitespace-nowrap">
                {moneyFormat(total)} ₺
            </span>
        );
    };

    return (
        <div className="quotation-page min-h-screen bg-slate-100 p-3 md:p-5">
            <style>
                {`
                    .quotation-page .p-card {
                        box-shadow: none;
                    }

                    .quotation-page .p-card-body {
                        padding: 0;
                    }

                    .quotation-page .p-card-content {
                        padding: 0;
                    }

                    .quotation-calendar-panel {
                        z-index: 12000 !important;
                    }

                    .quotation-page .p-inputtext,
                    .quotation-page .p-inputnumber-input {
                        width: 100%;
                    }

                    .quotation-page .p-button {
                        white-space: nowrap;
                    }

                    .quotation-page .p-datatable .p-datatable-thead > tr > th {
                        background: #f8fafc;
                        color: #64748b;
                        font-size: 10px;
                        font-weight: 900;
                        letter-spacing: .08em;
                        text-transform: uppercase;
                        border-color: #e2e8f0;
                    }

                    .quotation-page .p-datatable .p-datatable-tbody > tr > td {
                        border-color: #f1f5f9;
                        padding-top: .85rem;
                        padding-bottom: .85rem;
                    }

                    .quotation-page .p-confirm-popup {
                        z-index: 13000 !important;
                    }

                    @media (max-width: 768px) {
                        .quotation-page .p-datatable-wrapper {
                            overflow-x: auto;
                        }

                        .quotation-page .p-datatable-table {
                            min-width: 760px;
                        }
                    }
                `}
            </style>

            <Toast ref={toast} appendTo={overlayAppendTo} />
            <ConfirmPopup />

            <div className="max-w-[1600px] mx-auto space-y-5">
                {/* HEADER */}
                <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
                    <div className="p-4 md:p-6 border-b border-slate-100 bg-gradient-to-br from-white via-white to-indigo-50">
                        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                            <div className="flex items-center gap-4 min-w-0">
                                <Button
                                    icon="pi pi-arrow-left"
                                    rounded
                                    text
                                    className="shrink-0 text-slate-500 hover:bg-slate-100"
                                    onClick={() => navigate("/quotations")}
                                />

                                <div className="w-13 h-13 md:w-14 md:h-14 !bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-100 shrink-0">
                                    <i className="pi pi-file-edit text-white text-xl md:text-2xl"></i>
                                </div>

                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h1 className="text-xl md:text-2xl font-black text-slate-900 uppercase tracking-tight m-0">
                                            {editId ? "Teklif Düzenle" : "Yeni Teklif"}
                                        </h1>

                                        {isDirty && (
                                            <Tag
                                                value="Kaydedilmemiş değişiklik"
                                                severity="warning"
                                                className="text-[10px] font-black"
                                            />
                                        )}
                                    </div>

                                    <div className="text-slate-500 text-xs font-bold uppercase tracking-widest mt-1">
                                        {editId ? `TEKLİF ID: #${editId}` : "Satış öncesi fiyatlandırma ve teklif hazırlama"}
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2 justify-start xl:justify-end">
                                {!lastQuotationId ? (
                                    <Button
                                        label="Kaydet"
                                        icon="pi pi-save"
                                        className="!bg-emerald-600 border-none hover:!bg-emerald-700 rounded-xl px-6 md:px-8 font-black text-sm shadow-lg shadow-emerald-100"
                                        onClick={handleSave}
                                        loading={saving}
                                        disabled={lines.length === 0}
                                    />
                                ) : (
                                    <>
                                        <Button
                                            label="PDF İndir"
                                            icon="pi pi-file-pdf"
                                            className="p-button-danger p-button-outlined rounded-xl font-bold text-xs"
                                            onClick={() => receiptRef.current?.downloadPDF(lastQuotationId)}
                                        />

                                        <Button
                                            label="Güncelle"
                                            icon="pi pi-refresh"
                                            className="p-button-secondary p-button-outlined rounded-xl font-bold text-xs"
                                            onClick={handleSave}
                                            loading={saving}
                                        />

                                        <Button
                                            label="Yeni Teklif"
                                            icon="pi pi-plus"
                                            className="!bg-indigo-600 border-none rounded-xl font-bold text-xs"
                                            onClick={() => {
                                                window.location.href = "/quotations/add";
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 p-4 md:p-6">
                        {/* LEFT SIDE */}
                        <div className="xl:col-span-4 2xl:col-span-3 space-y-5">
                            {/* GENERAL INFO */}
                            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-4 md:p-5">
                                <div className="flex items-center justify-between gap-3 mb-4">
                                    <h3 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.18em] flex items-center gap-2 m-0">
                                        <i className="pi pi-user text-indigo-500"></i>
                                        Genel Bilgiler
                                    </h3>

                                    {selectedCustomer && (
                                        <Tag
                                            value="Müşteri seçildi"
                                            severity="success"
                                            className="text-[10px] font-black"
                                        />
                                    )}
                                </div>

                                <div className="space-y-4">
                                    <div className="relative">
                                        <label className="text-[10px] text-slate-500 font-black uppercase tracking-wider block ml-1 mb-1.5">
                                            Müşteri Seçimi *
                                        </label>

                                        <span className="p-input-icon-left w-full">
                                            <i className="ml-3 pi pi-search text-slate-400"></i>
                                            <InputText
                                                ref={customerInputRef}
                                                value={customerQuery}
                                                placeholder="Müşteri adı ile ara..."
                                                className="w-full rounded-xl border-slate-200 focus:border-indigo-500 font-bold text-sm py-3 pl-10"
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setCustomerQuery(value);
                                                    setSelectedCustomer(null);
                                                    setIsDirty(true);
                                                    searchCustomers(value);
                                                }}
                                                onFocus={() => {
                                                    if (customers.length > 0) setShowCustomerList(true);
                                                }}
                                            />
                                        </span>

                                        {showCustomerList && (
                                            <div className="absolute top-full left-0 right-0 mt-2 bg-white shadow-2xl rounded-2xl border border-slate-100 z-[15000] max-h-60 overflow-y-auto">
                                                {customers.map((c) => (
                                                    <div
                                                        key={c.id}
                                                        onMouseDown={() => selectCustomer(c)}
                                                        className="px-4 py-3 hover:bg-indigo-50 cursor-pointer border-b border-slate-50 last:border-none transition-colors"
                                                    >
                                                        <div className="font-black text-sm text-slate-700 truncate">
                                                            {c.name}
                                                        </div>

                                                        <div className="text-[11px] text-slate-400 mt-0.5">
                                                            {c.phone || c.email || "İletişim bilgisi yok"}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="text-[10px] text-slate-500 font-black uppercase tracking-wider block ml-1 mb-1.5">
                                            Teklif Numarası
                                        </label>

                                        <InputText
                                            value={header.quotationNo}
                                            onChange={(e) => updateHeader("quotationNo", e.target.value)}
                                            placeholder="Boş bırakılırsa otomatik"
                                            className="w-full rounded-xl border-slate-200 text-sm py-3"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
                                        <div>
                                            <label className="text-[10px] text-slate-500 font-black uppercase tracking-wider block ml-1 mb-1.5">
                                                Teklif Tarihi
                                            </label>

                                            <Calendar
                                                value={header.quotationDate}
                                                onChange={(e) => updateHeader("quotationDate", e.value)}
                                                showIcon
                                                dateFormat="dd.mm.yy"
                                                className="w-full"
                                                inputClassName="rounded-xl border-slate-200 py-3 text-sm"
                                                {...calendarOverlayProps}
                                            />
                                        </div>

                                        <div>
                                            <label className="text-[10px] text-red-500 font-black uppercase tracking-wider block ml-1 mb-1.5">
                                                Geçerlilik Tarihi *
                                            </label>

                                            <Calendar
                                                value={header.validUntil}
                                                onChange={(e) => updateHeader("validUntil", e.value)}
                                                showIcon
                                                dateFormat="dd.mm.yy"
                                                className="w-full"
                                                inputClassName="rounded-xl border-red-100 py-3 text-sm bg-red-50/40"
                                                minDate={new Date()}
                                                {...calendarOverlayProps}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* SUMMARY */}
                            <div className="bg-gradient-to-br from-indigo-950 via-indigo-900 to-slate-950 text-white p-5 rounded-3xl shadow-xl shadow-indigo-100 xl:sticky xl:top-5">
                                <div className="flex items-center justify-between mb-5">
                                    <h3 className="text-[11px] font-black text-indigo-300 uppercase tracking-[0.2em] m-0">
                                        Özet Bilgiler
                                    </h3>

                                    <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center">
                                        <i className="pi pi-calculator text-indigo-200"></i>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex justify-between items-center text-indigo-200 text-[12px] font-bold uppercase">
                                        <span>Ara Toplam</span>
                                        <span className="text-white">{moneyFormat(totals.sub)} ₺</span>
                                    </div>

                                    <div className="flex justify-between items-center text-indigo-200 text-[12px] font-bold uppercase">
                                        <span>KDV Toplam</span>
                                        <span className="text-white">{moneyFormat(totals.vat)} ₺</span>
                                    </div>

                                    <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
                                        <label className="text-[10px] font-black uppercase text-orange-300 block mb-2">
                                            Genel İndirim
                                        </label>

                                        <InputNumber
                                            value={generalDiscount}
                                            onValueChange={(e) => {
                                                setGeneralDiscount(e.value || 0);
                                                setIsDirty(true);
                                            }}
                                            className="w-full"
                                            inputClassName="bg-indigo-950/70 border border-white/10 text-white text-right font-black text-sm rounded-xl py-2 px-3"
                                            mode="currency"
                                            currency="TRY"
                                            locale="tr-TR"
                                            min={0}
                                        />
                                    </div>

                                    <div className="pt-4 border-t border-white/10">
                                        <span className="text-[10px] font-black uppercase text-indigo-300 mb-1 block">
                                            Ödenecek Toplam
                                        </span>

                                        <div className="text-right">
                                            <span className="text-3xl md:text-4xl font-black text-emerald-400 tracking-tighter leading-none">
                                                {moneyFormat(totals.grand)}
                                            </span>
                                            <span className="text-sm font-black text-emerald-300 ml-1">₺</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT SIDE */}
                        <div className="xl:col-span-8 2xl:col-span-9 space-y-5">
                            {/* ADD PRODUCT */}
                            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-4 md:p-5">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-4">
                                    <h3 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.18em] flex items-center gap-2 m-0">
                                        <i className="pi pi-box text-indigo-500"></i>
                                        Ürün ve Hizmet Ekle
                                        <Button
                                            type="button"
                                            icon="pi pi-info-circle"
                                            rounded
                                            text
                                            severity="secondary"
                                            className="w-6 h-6 text-slate-400 hover:text-slate-600 p-0"
                                            tooltip="Fiyat Kısayolları: Ürün seçtikten sonra klavyeden Alt + 1 (Standart), Alt + 2, Alt + 3... tuşlarına basarak ürün fiyat seviyeleri arasında hızlıca geçiş yapabilirsiniz."
                                            tooltipOptions={{ position: "bottom" }}
                                        />
                                    </h3>

                                    {newLine.product && (
                                        <Tag
                                            value="Ürün seçildi"
                                            severity="success"
                                            className="text-[10px] font-black"
                                        />
                                    )}
                                </div>

                                <div className="space-y-4">
                                    <div className="relative">
                                        <span className="p-input-icon-left w-full">
                                            <i className="ml-3 pi pi-search text-slate-400"></i>
                                            <InputText
                                                ref={productInputRef}
                                                value={productQuery}
                                                placeholder="Ürün adı veya barkod ile ara..."
                                                className="w-full rounded-2xl py-3.5 pl-10 font-bold border-slate-200 focus:ring-2 focus:ring-indigo-500 shadow-sm text-sm md:text-base"
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setProductQuery(value);
                                                    updateNewLine("product", null);
                                                    searchProducts(value);
                                                }}
                                                onFocus={() => {
                                                    if (products.length > 0) setShowList(true);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (!showList || products.length === 0) return;

                                                    if (e.key === "ArrowDown") {
                                                        e.preventDefault();
                                                        setHighlightIndex((prev) => Math.min(prev + 1, products.length - 1));
                                                    }

                                                    if (e.key === "ArrowUp") {
                                                        e.preventDefault();
                                                        setHighlightIndex((prev) => Math.max(prev - 1, 0));
                                                    }

                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        selectProduct(products[highlightIndex]);
                                                    }

                                                    if (e.key === "Escape") {
                                                        setShowList(false);
                                                    }
                                                }}
                                            />
                                        </span>

                                        {showList && (
                                            <div className="absolute top-full left-0 right-0 mt-2 bg-white shadow-2xl rounded-2xl border border-slate-100 z-[15000] max-h-72 overflow-y-auto">
                                                {products.map((p, i) => (
                                                    <div
                                                        key={p.id}
                                                        onMouseDown={() => selectProduct(p)}
                                                        className={`flex items-center justify-between gap-4 px-5 py-4 cursor-pointer border-b border-slate-50 last:border-none transition-colors ${
                                                            i === highlightIndex ? "bg-indigo-50" : "hover:bg-slate-50"
                                                        }`}
                                                    >
                                                        <div className="flex flex-col min-w-0">
                                                            <span className="font-black text-sm text-slate-700 uppercase tracking-tight truncate">
                                                                {p.name}
                                                            </span>

                                                            <span className="text-[10px] text-slate-400 font-bold uppercase mt-0.5">
                                                                Birim: {p.unit || "Adet"}
                                                            </span>
                                                        </div>

                                                        <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-lg text-xs font-black whitespace-nowrap">
                                                            {moneyFormat(p.salePrice)} ₺
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>



                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-12 gap-3 items-end">
                                        <div className="xl:col-span-2">
                                            <label className="text-[10px] text-slate-500 font-black uppercase tracking-wider block ml-1 mb-1">
                                                Miktar
                                            </label>

                                            <InputNumber
                                                ref={quantityRef}
                                                value={newLine.quantity}
                                                onValueChange={(e) => updateNewLine("quantity", e.value || 0)}
                                                className="w-full"
                                                inputClassName="rounded-xl border-slate-200 py-3 text-center font-black text-sm"
                                                min={0}
                                                minFractionDigits={0}
                                                maxFractionDigits={3}
                                            />
                                        </div>

                                        <div className="xl:col-span-3">
                                            <label className="text-[10px] text-slate-500 font-black uppercase tracking-wider block ml-1 mb-1">
                                                Birim Fiyat
                                            </label>

                                            <InputNumber
                                                ref={priceRef}
                                                value={newLine.unitPrice}
                                                onValueChange={(e) => updateNewLine("unitPrice", e.value || 0)}
                                                mode="currency"
                                                currency="TRY"
                                                locale="tr-TR"
                                                className="w-full"
                                                inputClassName="rounded-xl border-slate-200 py-3 font-black text-sm"
                                                min={0}
                                            />
                                        </div>

                                        <div className="xl:col-span-2">
                                            <label className="text-[10px] text-slate-500 font-black uppercase tracking-wider block ml-1 mb-1">
                                                KDV %
                                            </label>

                                            <InputNumber
                                                value={newLine.vatRate}
                                                onValueChange={(e) => updateNewLine("vatRate", e.value || 0)}
                                                suffix=" %"
                                                className="w-full"
                                                inputClassName="rounded-xl border-slate-200 py-3 text-center font-black text-sm"
                                                min={0}
                                                max={100}
                                            />
                                        </div>

                                        <div className="sm:col-span-2 xl:col-span-3">
                                            <label className="text-[10px] text-slate-500 font-black uppercase tracking-wider block ml-1 mb-1">
                                                Açıklama / Not
                                            </label>

                                            <InputText
                                                value={newLine.note}
                                                onChange={(e) => updateNewLine("note", e.target.value)}
                                                className="w-full rounded-xl border-slate-200 py-3 text-sm"
                                                placeholder="Bu satıra özel not..."
                                            />
                                        </div>

                                        <div className="sm:col-span-2 xl:col-span-2">
                                            <Button
                                                label="Ekle"
                                                icon="pi pi-plus"
                                                className="!bg-indigo-600 hover:!bg-indigo-700 border-none rounded-xl w-full h-[48px] font-black shadow-lg shadow-indigo-100"
                                                onClick={handleAddLine}
                                                disabled={!newLine.product}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* TABLE */}
                            <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 p-4 border-b border-slate-100">
                                    <div>
                                        <h3 className="text-sm font-black text-slate-800 m-0">
                                            Teklif Satırları
                                        </h3>
                                        <p className="text-xs text-slate-400 m-0 mt-1">
                                            Eklenen ürün ve hizmet kalemleri
                                        </p>
                                    </div>

                                    <Tag
                                        value={`${lines.length} satır`}
                                        severity={lines.length > 0 ? "info" : "secondary"}
                                        className="font-black text-[10px] self-start md:self-center"
                                    />
                                </div>

                                <DataTable
                                    value={lines}
                                    size="small"
                                    className="p-datatable-sm"
                                    emptyMessage="Teklif satırı bulunamadı. Ürün arayıp teklif satırı ekleyebilirsiniz."
                                    responsiveLayout="scroll"
                                >
                                    <Column
                                        field="productName"
                                        header="Ürün / Hizmet"
                                        body={productNameTemplate}
                                        style={{ minWidth: "240px" }}
                                    />

                                    <Column
                                        field="quantity"
                                        header="Miktar"
                                        body={quantityTemplate}
                                        bodyClassName="text-center"
                                        headerClassName="text-center"
                                        style={{ width: "110px" }}
                                    />

                                    <Column
                                        field="unitPrice"
                                        header="Birim Fiyat"
                                        body={unitPriceTemplate}
                                        style={{ width: "150px" }}
                                    />

                                    <Column
                                        field="vatRate"
                                        header="KDV"
                                        body={vatTemplate}
                                        bodyClassName="text-center"
                                        headerClassName="text-center"
                                        style={{ width: "90px" }}
                                    />

                                    <Column
                                        header="Toplam"
                                        body={totalTemplate}
                                        style={{ width: "150px" }}
                                    />

                                    <Column
                                        header=""
                                        bodyStyle={{ textAlign: "right" }}
                                        style={{ width: "70px" }}
                                        body={(row) => (
                                            <Button
                                                icon="pi pi-trash"
                                                rounded
                                                text
                                                severity="danger"
                                                tooltip="Satırı sil"
                                                tooltipOptions={{ position: "left" }}
                                                onClick={(e) => deleteLine(e, row.id)}
                                            />
                                        )}
                                    />
                                </DataTable>
                            </div>

                            {/* NOTES */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <Card className="rounded-3xl border border-slate-200 bg-slate-50 shadow-sm">
                                    <div className="p-4">
                                        <label className="text-[10px] text-slate-500 font-black uppercase tracking-wider block mb-2">
                                            Teklif Notu
                                        </label>

                                        <InputTextarea
                                            value={header.note}
                                            onChange={(e) => updateHeader("note", e.target.value)}
                                            rows={4}
                                            autoResize
                                            className="w-full rounded-2xl border-slate-200 text-sm p-3"
                                            placeholder="Müşteriye iletilecek genel not..."
                                        />
                                    </div>
                                </Card>

                                <Card className="rounded-3xl border border-slate-200 bg-slate-50 shadow-sm">
                                    <div className="p-4">
                                        <label className="text-[10px] text-slate-500 font-black uppercase tracking-wider block mb-2">
                                            Şartlar ve Koşullar
                                        </label>

                                        <InputTextarea
                                            value={header.terms}
                                            onChange={(e) => updateHeader("terms", e.target.value)}
                                            rows={4}
                                            autoResize
                                            className="w-full rounded-2xl border-slate-200 text-sm p-3"
                                            placeholder="Örn: %50 peşin, teslimat 7 iş günü..."
                                        />
                                    </div>
                                </Card>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <QuotationReceipt ref={receiptRef} />
        </div>
    );
}